<template>
  <v-card elevation="1">
    <v-card-text class="help-guide pa-sm-6 pa-4">
      <div class="d-flex align-center justify-space-between ga-3 mb-6">
        <v-btn variant="text" color="primary" prepend-icon="mdi-arrow-left" @click="goBack">
          Усі інструкції
        </v-btn>
        <v-btn
          v-if="mobile && headings.length"
          variant="outlined"
          color="primary"
          prepend-icon="mdi-format-list-bulleted"
          @click="mobileContentsOpen = !mobileContentsOpen">
          Зміст
        </v-btn>
      </div>

      <b-section-loader :loading="$loading.isLoading('helpGuide')">
        <div v-if="documentHtml" class="help-guide__layout">
          <aside v-if="!mobile && headings.length" class="help-guide__contents">
            <div class="help-guide__contents-title">Зміст</div>
            <a
              v-for="heading in headings"
              :key="heading.id"
              :class="`help-guide__contents-link help-guide__contents-link--${heading.level}`"
              :href="`#${heading.id}`"
              @click.prevent="scrollToHeading(heading.id)">
              {{ heading.text }}
            </a>
          </aside>

          <v-expand-transition>
            <div v-if="mobile && mobileContentsOpen" class="help-guide__mobile-contents">
              <div class="help-guide__contents-title">Зміст</div>
              <a
                v-for="heading in headings"
                :key="heading.id"
                :class="`help-guide__contents-link help-guide__contents-link--${heading.level}`"
                :href="`#${heading.id}`"
                @click.prevent="scrollToHeading(heading.id)">
                {{ heading.text }}
              </a>
            </div>
          </v-expand-transition>

          <article ref="content" class="help-guide__content" v-html="documentHtml"></article>
        </div>
      </b-section-loader>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, nextTick, PropType } from 'vue'
import { useDisplay } from 'vuetify'
import { BSectionLoader } from 'best-modules/components'
import { getHelpGuide } from '@/request/help'

type Heading = {
  id: string
  level: number
  text: string
}

export default defineComponent({
  name: 'HelpGuide',
  components: {
    BSectionLoader,
  },
  props: {
    guide: {
      type: String as PropType<'v1-web' | 'v1-excel' | 'v2-web' | 'v2-excel'>,
      required: true,
    },
  },
  data() {
    return {
      documentHtml: '',
      headings: [] as Heading[],
      mobileContentsOpen: false,
    }
  },
  setup() {
    const { mobile } = useDisplay()

    return { mobile }
  },
  watch: {
    guide: {
      immediate: true,
      handler() {
        this.loadGuide()
      },
    },
  },
  methods: {
    async loadGuide() {
      this.documentHtml = ''
      this.headings = []
      this.mobileContentsOpen = false

      const guide = await getHelpGuide(this.guide)
      this.documentHtml = guide.html

      await nextTick()
      this.prepareContent()
    },
    prepareContent() {
      const content = this.$refs.content as HTMLElement | undefined

      if (!content) {
        return
      }

      const headingCounters: Record<string, number> = {}
      this.headings = Array.from(content.querySelectorAll('h1, h2, h3')).map(
        (element, index) => {
          const text = element.textContent?.trim() || `Розділ ${index + 1}`
          const baseId = text
            .toLocaleLowerCase()
            .replace(/[^a-zа-яіїєґ0-9]+/gi, '-')
            .replace(/(^-|-$)/g, '') || `section-${index + 1}`
          const duplicate = headingCounters[baseId] || 0
          headingCounters[baseId] = duplicate + 1
          const id = duplicate ? `${baseId}-${duplicate + 1}` : baseId

          element.id = id

          return {
            id,
            text,
            level: Number(element.tagName.substring(1)),
          }
        }
      )

      content.querySelectorAll('table').forEach(table => {
        if (table.parentElement?.classList.contains('help-guide__table-wrapper')) {
          return
        }

        const wrapper = document.createElement('div')
        wrapper.className = 'help-guide__table-wrapper'
        table.parentNode?.insertBefore(wrapper, table)
        wrapper.appendChild(table)
      })

      content.querySelectorAll('a[href^="http"]').forEach(link => {
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
      })
    },
    scrollToHeading(id: string) {
      document.getElementById(id)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
      this.mobileContentsOpen = false
      window.history.replaceState(null, '', `#${id}`)
    },
    goBack() {
      this.$router.push({ name: 'help' })
    },
  },
})
</script>

<style scoped lang="scss">
.help-guide {
  max-width: 1440px;
  margin: 0 auto;

  &__layout {
    display: grid;
    grid-template-columns: minmax(200px, 250px) minmax(0, 1fr);
    gap: 32px;
    align-items: start;
  }

  &__contents,
  &__mobile-contents {
    background: var(--light-grey);
    border-left: 2px solid rgb(var(--v-theme-primary));
    border-radius: 4px;
    padding: 16px;
  }

  &__contents {
    position: sticky;
    top: 20px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }

  &__contents-title {
    color: rgb(var(--v-theme-on-surface));
    font-weight: 700;
    margin-bottom: 10px;
  }

  &__contents-link {
    display: block;
    color: var(--grey);
    font-size: 0.9rem;
    line-height: 1.35;
    margin: 8px 0;
    text-decoration: none;

    &:hover {
      color: rgb(var(--v-theme-primary));
      text-decoration: underline;
    }

    &--1 {
      font-weight: 700;
    }

    &--3 {
      margin-left: 12px;
    }
  }

  &__mobile-contents {
    margin-bottom: 24px;
  }

  &__content {
    min-width: 0;
    overflow-x: hidden;

    :deep(h1),
    :deep(h2),
    :deep(h3) {
      scroll-margin-top: 20px;
    }

    :deep(h1) {
      font-size: 1.75rem;
      margin: 0 0 24px;
    }

    :deep(h2) {
      font-size: 1.4rem;
      margin: 36px 0 16px;
    }

    :deep(h3) {
      font-size: 1.15rem;
      margin: 28px 0 12px;
    }

    :deep(p),
    :deep(li) {
      line-height: 1.6;
    }

    :deep(a) {
      color: rgb(var(--v-theme-primary));
    }

    :deep(code) {
      padding: 2px 5px;
      background: var(--light-grey);
      border-radius: 3px;
      color: rgb(var(--v-theme-on-surface));
    }

    :deep(pre) {
      overflow-x: auto;
      padding: 16px;
      background: var(--light-grey);
      border-radius: 6px;
    }

    :deep(pre code) {
      padding: 0;
      background: transparent;
    }

    :deep(blockquote) {
      margin: 20px 0;
      padding: 12px 16px;
      border-left: 2px solid #ffb200;
      background: var(--light-grey);
      color: var(--grey);
    }

    :deep(.help-guide__table-wrapper) {
      max-width: 100%;
      margin: 20px 0;
      overflow-x: auto;
      border: 1px solid var(--light-grey);
      border-radius: 6px;
    }

    :deep(table) {
      width: 100%;
      min-width: 720px;
      border-collapse: collapse;
    }

    :deep(th),
    :deep(td) {
      padding: 10px 12px;
      text-align: left;
      vertical-align: top;
      border-bottom: 1px solid var(--light-grey);
      line-height: 1.45;
    }

    :deep(th) {
      background: rgba(var(--v-theme-primary), 0.06);
      font-weight: 700;
    }

    :deep(tr:last-child td) {
      border-bottom: 0;
    }
  }
}

@media (max-width: 959px) {
  .help-guide {
    &__layout {
      display: block;
    }
  }
}
</style>
